import { getPriceGivenIntent } from '@src/components/Ladder/utils';
import type { PrimeOMSParams } from '@src/components/OMS/NewOrder/types';
import { getMultilegInstrumentType } from '@src/components/OMS/NewOrder/utils/mappers';
import { OMSView } from '@src/components/OMS/OMSView';
import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import { CardStatusIndicator } from '@src/components/StatusIndicator';
import { MarketDataStatusTable } from '@src/components/StatusTable';
import { SymbolHeader } from '@src/components/SymbolDisplay/SymbolHeader';
import { SymbolSubhead } from '@src/components/SymbolDisplay/SymbolSubhead';
import type { SymbolDisplayProps } from '@src/components/SymbolDisplay/types';
import { SymbolSelector } from '@src/components/SymbolSelector';
import { useSymbolSelectorWidth } from '@src/components/SymbolSelector/useSymbolSelectorWidth';
import { TopOfBook, type TopOfBookProps } from '@src/components/TopOfBook';
import { useMarketCardRfdDragIslandProps } from '@src/containers/Trading/Markets/MarketDataCards/MarketCardRfdDragContext';
import { useDefaultSubAccount, useMarketDataSnapshot } from '@src/hooks';
import { useIsPricingModeBPS } from '@src/hooks/useIsPricingModeBPS';
import { useMarketsContext, useSubAccounts } from '@src/providers';
import { useDisplaySettings, useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { getMarketDataLevelPrices } from '@src/utils/getMarketDataLevelPrice';
import {
  AggressorFlagEnum,
  ConnectionType,
  Flex,
  FormControlSizes,
  HStack,
  MarketDataCardLabel,
  MarketDataCardSummary,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  OrdTypeEnum,
  Popover,
  MarketDataCardHeader as SharedMarketDataCardHeader,
  SideEnum,
  TOP_OF_BOOK_SIZE,
  UnifiedLiquidityEnum,
  VStack,
  isMultileg,
  isSecurityCME,
  isSpot,
  toBigWithDefault,
  useAggregationsContext,
  useCallbackRef,
  useMarketAccountsContext,
  useMixpanel,
  useObservable,
  useObservableRef,
  useObservableValue,
  usePopoverState,
  useSecuritiesContext,
  useUnifiedLiquidity,
  useUnifiedLiquidityAvailableSymbols,
  type ISubaccount,
  type OrderIntent,
  type Security,
} from '@talos/kyoko';
import type { UseComboboxStateChange } from 'downshift';
import { get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { merge } from 'rxjs';
import { useCloneMultilegSymbol } from '../../hooks/useCloneMultilegSymbol';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { useNavigateToDeepDiveOrMultileg } from '../../hooks/useNavigateToDeepDiveOrMultileg';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import { useOrderPresets, type IOrderPreset } from '../../providers/OrderPresetsContext';
import { DisclaimerTooltip, cmeDisclaimer } from '../DisclaimerTooltip';
import { MultilegComboType } from '../MultilegCombo/enums';
import { primeNewRFQForm } from '../OMS/NewRFQ/RFQSlice';
import { openView } from '../OMS/OMSSlice';
import { useOrderPresetSearchSelectOptions } from '../OrderPresets/OrderPresetSearchSelect';
import { useSubAccountSearchSelectOptions } from '../SubAccounts/useSubAccountSearchSelectOptions';
import { ClickToTradeOrderPreview } from './ClickToTradeOrderPreview';
import { MarketDataCardActionButtons } from './components/MarketDataCardActionButtons';
import { MarketDataCardControlRow } from './components/MarketDataCardControlRow';
import { useMarketDataCardMarketSelectorContext } from './MarketDataCardMarketSelectorContext';
import { MarketDataCardMenu } from './MarketDataCardMenu';
import { AVA_MARKET_DATA_CARD_MDS_THROTTLE, type MarketDataCardHeaderProps } from './types';
import { useMarketDataCard } from './useMarketDataCard';
import { useMarketDataCardHeaderSymbolChange } from './useMarketDataCardHeaderSymbolChange';

const TOP_OF_BOOK_STYLE = { padding: '0 1rem' };

export const MarketDataCardHeader = ({
  config,
  security,
  activeOrder,
  isDraggable,
  defaultSubAccountId,
  onClickPrice,
  onCancelActive,
  onClickSettings,
  onSaveCard,
  onDelete,
  onOpenMarketSelector,
}: MarketDataCardHeaderProps) => {
  const marketCardRfdIslandProps = useMarketCardRfdDragIslandProps();
  const {
    id,
    selectedMarketAccounts,
    orderPresetID,
    selectedMarkets,
    symbol,
    topOfBookSizeBucket,
    title,
    unifiedLiquidity,
    alwaysDisplayBestBidOffer,
    subAccount,
  } = config;

  const { useNewMarketSelector, enableDefaultMarketCardSubAccount } = useTradingSettings();
  const isInMobileMode = useIsMobileLayout();
  const { DefaultPriceIncrement } = security || {};

  const { aggregationsByName } = useAggregationsContext();
  const { subAccountsByName } = useSubAccounts();
  const subAccountOptions = useSubAccountSearchSelectOptions();
  const {
    selectedMarketOrAccountNames,
    selectedAggregationID,
    selectedAggregationLabel,
    favoriteAggregationPresets,
    selectAggregationPreset,
  } = useMarketDataCardMarketSelectorContext();

  const mixpanel = useMixpanel();

  const symbolSelectorDropdownWidth = useSymbolSelectorWidth();

  const handleHeaderSymbolSelectorOpenChange = useCallback(
    (changes: UseComboboxStateChange<Security>) => {
      if (changes.isOpen) {
        mixpanel.track(MixpanelEvent.OpenSymbolSelectorFromMarketCard, {
          [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
        });
      }
    },
    [mixpanel]
  );

  const dispatch = useAppStateDispatch();

  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();

  const isPricingModeBPS = useIsPricingModeBPS(security);

  const { canSubmitOrder, canViewOrder, canRequestRFQ } = useMarketDataCard(security);

  const { isMarketSupported, marketDisplayNameByName } = useMarketsContext();
  const { marketAccountDisplayNameByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { getOrderCurrency } = useGetOrderCurrency();

  const securityMarkets = security?.Markets || [];
  const isRfqSupported = isSpot(security) && securityMarkets.some(m => isMarketSupported(m, ConnectionType.RFQ));

  const unifiedLiquidityToken = useUnifiedLiquidity(symbol);

  const availableSymbols = useUnifiedLiquidityAvailableSymbols({
    marketAccounts: selectedMarketAccounts,
    markets: selectedMarkets,
    symbol: symbol,
  });

  // Based on Unified Liquidity, creates list of underlying quote currencies including security symbol.
  const quoteCurrencies = useMemo(() => {
    if (unifiedLiquidity !== UnifiedLiquidityEnum.Enabled || !unifiedLiquidityToken || !security) {
      return [];
    }

    const quoteCurrenciesSet = new Set<string>();

    for (const availableSymbol of availableSymbols) {
      const availableSecurity = securitiesBySymbol.get(availableSymbol);
      if (availableSecurity) {
        quoteCurrenciesSet.add(availableSecurity.QuoteCurrency);
      }
    }

    return Array.from(quoteCurrenciesSet);
  }, [unifiedLiquidity, unifiedLiquidityToken, security, availableSymbols, securitiesBySymbol]);

  const subscriptionAddress = `${id}/${TOP_OF_BOOK_SIZE}`;

  // We don't need to subscribe if we're always showing the best bid offer
  const { marketDataSnapshots, tradeDirections, marketDataErrors } = useMarketDataSnapshot(
    alwaysDisplayBestBidOffer
      ? null
      : {
          marketAccounts: selectedMarketAccounts,
          markets: selectedMarkets,
          priceIncrement: DefaultPriceIncrement,
          symbol,
          quantity: topOfBookSizeBucket,
          tag: subscriptionAddress,
          includeTradeDirection: true,
          currency: undefined,
          depth: undefined,
          sizeBuckets: undefined,
          unifiedLiquidity,
          throttle: AVA_MARKET_DATA_CARD_MDS_THROTTLE,
        }
  );
  const marketDataSnapshot = useObservableRef(() => marketDataSnapshots, [marketDataSnapshots]);

  const bboSubscriptionAddress = `${id}/BBO`;

  const bid: string | undefined = get(marketDataSnapshot.current, `Bids[0].Price`);
  const offer: string | undefined = get(marketDataSnapshot.current, `Offers[0].Price`);

  // Wait until we have a snapshot to determine if we should use the BBO snapshot
  // We still need to keep the original subscription going if liquidity improves
  const useBBOSnapshot =
    alwaysDisplayBestBidOffer ||
    (marketDataSnapshot.current && toBigWithDefault(bid, 0).eq(0) && toBigWithDefault(offer, 0).eq(0));

  // Best Bid Offer snapshot for Depth = 1
  const bboSnapshot = useMarketDataSnapshot(
    useBBOSnapshot
      ? {
          markets: selectedMarkets,
          marketAccounts: selectedMarketAccounts,
          priceIncrement: DefaultPriceIncrement,
          symbol,
          currency: undefined,
          tag: bboSubscriptionAddress,
          includeTradeDirection: true,
          depth: 1,
          unifiedLiquidity,
          throttle: AVA_MARKET_DATA_CARD_MDS_THROTTLE,
        }
      : null
  );

  const currentMarketDataSnapshot = useObservableRef(() => {
    return useBBOSnapshot && bboSnapshot?.marketDataSnapshots ? bboSnapshot.marketDataSnapshots : marketDataSnapshots;
  }, [useBBOSnapshot, marketDataSnapshots, bboSnapshot.marketDataSnapshots]);

  /**
   * Since either one of these merged observables will be active, we merge them and use it for showing statuses.
   */
  const statusCarryingMarketDataSnapshots = useObservable(
    () => merge(bboSnapshot.marketDataSnapshots, marketDataSnapshots),
    [bboSnapshot.marketDataSnapshots, marketDataSnapshots]
  );
  const statusCarryingMarketDataErrors = useObservableValue(
    () => merge(bboSnapshot.marketDataErrors, marketDataErrors),
    [bboSnapshot.marketDataErrors, marketDataErrors]
  );

  const handleTopOfBookSizeUpdate = useCallback<NonNullable<TopOfBookProps['onUpdateSize']>>(
    (index, newSize) => {
      mixpanel.track(MixpanelEvent.ChangeTopOfBookSize, { [MixpanelEventProperty.Size]: newSize });
      onSaveCard({ ...config, topOfBookSizeBucket: newSize });
    },
    [config, mixpanel, onSaveCard]
  );

  const handleTopOfBookClickPrice = useCallback<NonNullable<TopOfBookProps['onClickPrice']>>(
    // @ts-expect-error - values are inconsistent between the parameter and the source
    values => onClickPrice({ values, source: MixpanelEventSource.TopOfBook }),
    [onClickPrice]
  );

  const popover = usePopoverState({
    usePortal: false,
    trigger: 'hover',
    placement: 'bottom',
    isSmall: true,
    delay: undefined,
  });

  const handleClickOnBid = () => primeOmsWithSnapshot({ side: SideEnum.Buy, intent: AggressorFlagEnum.Passive });

  const handleClickOnOffer = () => primeOmsWithSnapshot({ side: SideEnum.Sell, intent: AggressorFlagEnum.Passive });

  const { defaultSubAccount } = useDefaultSubAccount(defaultSubAccountId);

  const handleClickOnRfq = () => {
    mixpanel.track(MixpanelEvent.OpenRfqForm);
    if (!security) {
      return;
    }
    const params: PrimeOMSParams = {
      ordType: OrdTypeEnum.LimitAllIn,
      group: '',
      symbol: security.Symbol,
      currency: security.BaseCurrency,
      orderQty: topOfBookSizeBucket,
      marketAccounts: selectedMarketAccounts,
      subAccount: subAccount || defaultSubAccount?.Name,
    };
    dispatch(openView(OMSView.RFQForm));
    dispatch(primeNewRFQForm(params));
  };

  const primeOmsWithSnapshot = ({ side, intent }: { side: SideEnum; intent: OrderIntent }) => {
    const snapshot = currentMarketDataSnapshot.current;
    if (snapshot == null) {
      return;
    }
    const { bidPrice, offerPrice } = getMarketDataLevelPrices({ snapshot, index: 0, isPricingModeBPS });
    const price = getPriceGivenIntent(side, intent, bidPrice ?? '', offerPrice ?? '');
    const currency = getOrderCurrency(security, side);
    onClickPrice({
      values: {
        side,
        currency,
        orderQty: topOfBookSizeBucket,
        price,
        intent,
      },
      source: MixpanelEventSource.BidOfferButtons,
      overrideSide: true,
      activateOrder: false,
    });
  };

  const shouldRenderFirm = !isMultileg(security);

  const labelText = useMemo(() => {
    const labelText = [
      [showAllInPrices, 'FEES'],
      [showFirmLiquidity && shouldRenderFirm, 'FIRM'],
    ]
      .filter(([state]) => state)
      .map(([, text]) => text)
      .join(' & ');

    return labelText;
  }, [showAllInPrices, showFirmLiquidity, shouldRenderFirm]);

  // Reduce the flex basis of both sides while also ensuring they're equal. This guarantees that the title will always be properly centered
  const slotFlexRule = showAllInPrices && showFirmLiquidity && shouldRenderFirm ? '0 0 74px' : '0 0 40px';

  // Order presets
  const { orderPresetsByID, orderPresetsList } = useOrderPresets();
  const handleChangeOrderPreset = useCallbackRef((preset?: IOrderPreset) => {
    mixpanel.track(MixpanelEvent.SelectOrderPreset, { source: MixpanelEventSource.MarketCardHeader });
    onSaveCard({ ...config, orderPresetID: preset?.id });
  });
  const orderPresetSelection = orderPresetID == null ? undefined : orderPresetsByID.get(orderPresetID);
  const orderPresetOptions = useOrderPresetSearchSelectOptions({ security });
  const { updateOrderPreset } = useOrderPresets();
  const handleToggleFavoritePreset = useCallbackRef((orderPreset: IOrderPreset) => {
    updateOrderPreset(orderPreset.id, {
      ...orderPreset,
      isFavorite: !orderPreset.isFavorite,
    });
  });

  // SubAccounts
  const handleSubAccountChange = useCallbackRef((subAccount?: ISubaccount) => {
    mixpanel.track(MixpanelEvent.SelectSubAccount, { source: MixpanelEventSource.MarketCardHeader });
    onSaveCard({ ...config, subAccount: subAccount?.Name });
  });
  const subAccountSelection = subAccount ? subAccountsByName.get(subAccount) : undefined;
  const handleChangeAggregation = useCallbackRef((aggregationID: string) => {
    selectAggregationPreset(aggregationID);
  });

  // Market Data Card Menu handlers
  const navigateToDeepDiveOrMultileg = useNavigateToDeepDiveOrMultileg();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const { confirmDelete } = useDeleteConfirmation();

  const handleOpenDeepDive = useCallbackRef(() => {
    navigateToDeepDiveOrMultileg(security, selectedMarketAccounts, selectedMarkets, undefined);
  });

  const handleDeleteCard = useCallbackRef(() => {
    confirmDelete({
      title: 'Delete Market Data Card',
      itemName: `the card for ${symbol}`,
      onConfirm: () => {
        mixpanel.track(MixpanelEvent.DeleteCard, {
          [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
        });
        onDelete(config);
      },
    });
  });

  const handleCreateMultileg = useCallbackRef((multilegType: MultilegComboType) => {
    if (!security) {
      return;
    }
    mixpanel.track(MixpanelEvent.CreateNewMultileg, {
      [MixpanelEventProperty.Type]: multilegType,
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
    navigateToMultilegCombo(multilegType, undefined, selectedMarketAccounts, selectedMarkets, [security.Symbol]);
  });

  const handleEditSymbol = useCallbackRef(() => {
    if (!security || !isMultileg(security)) {
      return;
    }
    mixpanel.track(MixpanelEvent.EditMultileg, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
    navigateToMultilegCombo(
      getMultilegInstrumentType(security.MultilegDetails?.SyntheticProductType) ?? MultilegComboType.SyntheticCross,
      security
    );
  });

  const cloneMultilegSymbol = useCloneMultilegSymbol(MixpanelEventSource.MarketCardHeader);
  const isMultilegSecurity = isMultileg(security);
  const handleOpenMarketSelector = useCallbackRef((event: MixpanelEvent) => {
    if (!onOpenMarketSelector) {
      return;
    }
    mixpanel.track(event, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
    onOpenMarketSelector();
  });
  const marketSelectorButtonOnClick = isMultilegSecurity
    ? handleEditSymbol
    : onOpenMarketSelector
      ? () => handleOpenMarketSelector(MixpanelEvent.OpenMarketSelectorFromMarketCardButton)
      : undefined;
  const marketSelectorMenuOnClick = onOpenMarketSelector
    ? () => handleOpenMarketSelector(MixpanelEvent.OpenMarketSelectorFromMarketCardMenu)
    : undefined;

  const handleMarketCardSymbolChange = useMarketDataCardHeaderSymbolChange({
    config,
    onSaveCard,
    onOpenMarketSelectorFromHeaderButton: () =>
      handleOpenMarketSelector(MixpanelEvent.OpenMarketSelectorFromMarketCardButton),
  });

  const marketName =
    selectedMarketAccounts.length > 0
      ? marketAccountDisplayNameByName.get(selectedMarketAccounts[0])
      : marketDisplayNameByName.get(selectedMarkets[0]);

  const showOrderPresets = orderPresetsList.length > 0;
  const showSubAccountSelector = enableDefaultMarketCardSubAccount && subAccountOptions.length > 0;
  const marketSelectorButtonLabel = isMultilegSecurity
    ? 'Edit Symbol'
    : selectedAggregationID
      ? (selectedAggregationLabel ??
        aggregationsByName?.get(selectedAggregationID)?.DisplayName ??
        selectedAggregationID)
      : 'Custom';
  const showNoMarketSelectionWarning =
    !isMultilegSecurity && selectedMarketOrAccountNames.length === 0 && selectedAggregationID == null;
  const showSmallControls = showOrderPresets || showSubAccountSelector || marketSelectorButtonOnClick != null;

  const actionButtonSize = showSmallControls ? FormControlSizes.Xxs : FormControlSizes.Small;
  const actionItems = canViewOrder ? (
    <VStack w="100%" alignItems="stretch" px="spacing16" gap="spacing4" flex="1">
      <div {...(marketCardRfdIslandProps ?? {})}>
        <MarketDataCardControlRow
          showMarketSelectorNavigation={!isMultilegSecurity}
          isEditSymbolMode={isMultilegSecurity}
          showOrderPresets={showOrderPresets}
          showSubAccountSelector={showSubAccountSelector}
          onMarketSelectorClick={marketSelectorButtonOnClick}
          aggregationPresets={favoriteAggregationPresets}
          selectedAggregationID={selectedAggregationID}
          onChangeAggregation={handleChangeAggregation}
          marketSelectorButtonLabel={marketSelectorButtonLabel}
          selectedMarketOrAccountNames={selectedMarketOrAccountNames}
          orderPresetSelection={orderPresetSelection}
          onChangeOrderPreset={handleChangeOrderPreset}
          onToggleFavoritePreset={handleToggleFavoritePreset}
          orderPresetOptions={orderPresetOptions}
          subAccountSelection={subAccountSelection}
          subAccountOptions={subAccountOptions}
          onSubAccountChange={handleSubAccountChange}
        />
      </div>
      <MarketDataCardActionButtons
        canSubmitOrder={canSubmitOrder}
        canRequestRFQ={canRequestRFQ}
        isRfqSupported={isRfqSupported}
        size={actionButtonSize}
        onClickBid={handleClickOnBid}
        onClickRfq={handleClickOnRfq}
        onClickOffer={handleClickOnOffer}
      />
      {!showSmallControls ? <div style={{ height: '8px' }} /> : null}
    </VStack>
  ) : null;

  const topLeftItems = labelText ? <MarketDataCardLabel>{labelText}</MarketDataCardLabel> : null;

  const topRightItems = (
    <HStack m="-spacing4" data-testid="top-right-items">
      {isSecurityCME(security) && <DisclaimerTooltip>{cmeDisclaimer}</DisclaimerTooltip>}
      {selectedMarkets.length || selectedMarketAccounts.length ? (
        <span data-testid="market-status-popover">
          <Popover {...popover}>
            <CardStatusIndicator
              selectedMarkets={selectedMarkets}
              selectedMarketAccounts={selectedMarketAccounts}
              snapshots={statusCarryingMarketDataSnapshots}
              websocketError={statusCarryingMarketDataErrors}
            />
            <MarketDataStatusTable
              snapshots={statusCarryingMarketDataSnapshots}
              websocketError={statusCarryingMarketDataErrors}
            />
          </Popover>
        </span>
      ) : (
        <CardStatusIndicator
          selectedMarkets={selectedMarkets}
          selectedMarketAccounts={selectedMarketAccounts}
          snapshots={statusCarryingMarketDataSnapshots}
          websocketError={statusCarryingMarketDataErrors}
        />
      )}
      <MarketDataCardMenu
        security={security}
        onOpenSettings={onClickSettings}
        onOpenDeepDive={handleOpenDeepDive}
        onDeleteCard={handleDeleteCard}
        onCreateMultileg={isInMobileMode ? undefined : handleCreateMultileg}
        onEditSymbol={isMultilegSecurity ? handleEditSymbol : undefined}
        onCloneSymbol={cloneMultilegSymbol}
        onOpenMarketSelector={marketSelectorMenuOnClick}
      />
    </HStack>
  );

  const symbolHeaderProps: SymbolDisplayProps = {
    security,
    title,
    quoteCurrencies,
    unifiedLiquidity,
    availableSymbols,
    multipleSelections: selectedMarketAccounts.length + selectedMarkets.length > 1,
    selectedAggregation:
      config.selectedAggregation && useNewMarketSelector
        ? aggregationsByName?.get(config.selectedAggregation)
        : undefined,
    selectedMarketAccounts,
    marketName,
    tooltipOnOverflow: true,
    showNoMarketSelectionWarning,
    onNoMarketSelectionWarningClick: showNoMarketSelectionWarning ? marketSelectorButtonOnClick : undefined,
  };

  return (
    <>
      <SharedMarketDataCardHeader
        actionItems={actionItems}
        isDraggable={isDraggable}
        security={security}
        slotFlexRule={slotFlexRule}
        topCenterItems={
          <SymbolSelector
            symbol={symbol ?? config.symbol}
            onSymbolChanged={handleMarketCardSymbolChange}
            size={FormControlSizes.Small}
            dropdownWidth={symbolSelectorDropdownWidth}
            dropdownPlacement="bottom-end"
            showGroups
            showFavorites
            portalize
            dropdownId="market-card-header-symbol-selector"
            onIsOpenChange={handleHeaderSymbolSelectorOpenChange}
            renderTrigger={({ openMenu }) => (
              <VStack
                alignItems="center"
                gap={0}
                cursor="pointer"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                onClick={openMenu}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openMenu();
                  }
                }}
                data-testid="market-card-symbol-selector-tour-anchor"
              >
                <SymbolHeader {...symbolHeaderProps} />
              </VStack>
            )}
          />
        }
        topSubheader={<SymbolSubhead {...symbolHeaderProps} />}
        topLeftItems={topLeftItems}
        topRightItems={topRightItems}
      >
        <TopOfBook
          style={TOP_OF_BOOK_STYLE}
          security={security}
          topOfBookSizeBucket={topOfBookSizeBucket}
          topOfBookSnapshots={marketDataSnapshots}
          alwaysShowBestBidOffer={alwaysDisplayBestBidOffer}
          tradeDirections={tradeDirections}
          showSideLabels={canViewOrder ? 'hover' : 'hidden'}
          activeOrder={activeOrder}
          isReadOnly={!canViewOrder}
          showAllInPrices={!!showAllInPrices}
          onClickPrice={handleTopOfBookClickPrice}
          onMouseLeavePrice={onCancelActive}
          onUpdateSize={handleTopOfBookSizeUpdate}
          bboSnapshots={bboSnapshot.marketDataSnapshots}
          isPricingModeBPS={isPricingModeBPS}
        />
      </SharedMarketDataCardHeader>

      {activeOrder || actionItems ? (
        <Flex alignItems="stretch" justifyContent="center" w="100%">
          {activeOrder && security ? (
            <MarketDataCardSummary>
              <ClickToTradeOrderPreview clickToTradePreviewOrder={activeOrder} security={security} />
            </MarketDataCardSummary>
          ) : (
            actionItems
          )}
        </Flex>
      ) : null}
    </>
  );
};
